import { useState } from "react"
import Dialog from "@mui/material/Dialog"
import {
  DialogTitle,
  Drawer,
  List,
  ListItemText,
  ListItemIcon,
  DialogActions,
  Button,
  ListItemButton,
} from "@mui/material"
import { ArrowForwardIosRounded } from "@mui/icons-material"
import { getDisplayableReadingState, useToggleTag } from "./helpers"
import { useTagIds } from "../tags/helpers"
import { ReadingStateState } from "@oboku/shared"
import { DownloadState } from "../download/states"
import { TagsSelectionDialog } from "../tags/TagsSelectionDialog"
import { useSignalValue } from "reactjrx"
import { libraryStateSignal } from "./books/states"
import { Checkbox } from "../common/Checkbox"

export const LibraryFiltersDrawer = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const [isTagsDialogOpened, setIsTagsDialogOpened] = useState(false)
  const [isReadingStateDialogOpened, setIsReadingStateDialogOpened] =
    useState(false)
  const { data: tags = [] } = useTagIds()
  const library = useSignalValue(libraryStateSignal)
  const selectedTags = library.tags
  const toggleTag = useToggleTag()

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        transitionDuration={0}
      >
        <div role="presentation">
          <List>
            <ListItemButton onClick={() => setIsTagsDialogOpened(true)}>
              <ListItemText
                primary="Tags"
                secondary={
                  (selectedTags?.length || 0) > 0
                    ? "You have selected tags"
                    : "Any"
                }
              />
              <ListItemIcon>
                <ArrowForwardIosRounded />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton onClick={() => setIsReadingStateDialogOpened(true)}>
              <ListItemText
                primary="Reading state"
                secondary={
                  library.readingStates.length > 0
                    ? library.readingStates
                        .map((s) => getDisplayableReadingState(s))
                        .join(", ")
                    : "Any"
                }
              />
              <ListItemIcon>
                <ArrowForwardIosRounded />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              onClick={() =>
                libraryStateSignal.update((state) => ({
                  ...state,
                  downloadState:
                    library.downloadState === DownloadState.Downloaded
                      ? undefined
                      : DownloadState.Downloaded,
                }))
              }
            >
              <ListItemText primary="Only show downloaded" />
              <ListItemIcon>
                <Checkbox
                  selected={library.downloadState === DownloadState.Downloaded}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              onClick={() =>
                libraryStateSignal.update((state) => ({
                  ...state,
                  isNotInterested:
                    library.isNotInterested === "only"
                      ? ("hide" as const)
                      : ("only" as const),
                }))
              }
            >
              <ListItemText primary="Only show not interested books" />
              <ListItemIcon>
                <Checkbox selected={library.isNotInterested === "only"} />
              </ListItemIcon>
            </ListItemButton>
          </List>
        </div>
      </Drawer>
      <TagsSelectionDialog
        hasBackNavigation
        open={isTagsDialogOpened}
        onClose={() => setIsTagsDialogOpened(false)}
        data={tags}
        selected={(tagId) => !!library.tags?.find((item) => item === tagId)}
        onItemClick={({ id }) => {
          toggleTag(id)
        }}
      />
      <ReadingStateDialog
        open={isReadingStateDialogOpened}
        onClose={() => setIsReadingStateDialogOpened(false)}
      />
    </>
  )
}

const ReadingStateDialog = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const library = useSignalValue(libraryStateSignal)
  const readingStates = [
    ReadingStateState.NotStarted,
    ReadingStateState.Reading,
    ReadingStateState.Finished,
  ]

  return (
    <Dialog onClose={onClose} aria-labelledby="simple-dialog-title" open={open}>
      <DialogTitle>Reading state</DialogTitle>
      {readingStates.map((readingState) => (
        <ListItemButton
          key={readingState}
          onClick={() => {
            if (library.readingStates.includes(readingState)) {
              libraryStateSignal.update((state) => ({
                ...state,
                readingStates: library.readingStates.filter(
                  (s) => s !== readingState,
                ),
              }))
            } else {
              libraryStateSignal.update((state) => ({
                ...state,
                readingStates: [...library.readingStates, readingState],
              }))
            }
          }}
        >
          <ListItemText primary={getDisplayableReadingState(readingState)} />
          <Checkbox selected={library.readingStates.includes(readingState)} />
        </ListItemButton>
      ))}
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  )
}
